import React, { useMemo } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import { Card, StatusPill } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { listPropertyReviews, listHostTestimonials } from '../../api/hostApi';
import { Star, MessageSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

function Stars({ rating }) {
  return (
    <div className="flex">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= (rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`} />
      ))}
    </div>
  );
}

export default function HostReviews() {
  return (
    <div>
      <PageHeader title="Reviews & testimonials" description="Guest feedback across your listings." />
      <Tabs defaultValue="reviews">
        <TabsList className="bg-white border border-slate-200 rounded-lg p-1">
          <TabsTrigger value="reviews">Property reviews</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
        </TabsList>
        <TabsContent value="reviews" className="mt-4"><ReviewsList /></TabsContent>
        <TabsContent value="testimonials" className="mt-4"><TestimonialsList /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReviewsList() {
  const { data, loading, error, refetch } = useApi(listPropertyReviews, []);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (rows.length === 0) return <EmptyResults icon={Star} title="No reviews yet" description="Once guests review your listings they will appear here." />;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {rows.map((r) => (
        <Card key={r.id} className="p-5">
          <div className="flex items-start gap-3">
            {r.customer?.avatar && <img src={r.customer.avatar} className="h-10 w-10 rounded-full object-cover" alt="" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-slate-900">{r.customer?.name}</div>
                <Stars rating={r.rating} />
                <StatusPill status={r.status} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{r.property?.title} · {r.created_at || r.createdAt}</div>
              {r.title && <div className="mt-2 font-medium text-slate-900">{r.title}</div>}
              <div className="text-sm text-slate-600 mt-1">{r.body}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function TestimonialsList() {
  const { data, loading, error, refetch } = useApi(listHostTestimonials, []);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (rows.length === 0) return <EmptyResults icon={MessageSquare} title="No testimonials" description="Approved testimonials will appear here." />;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {rows.map((t) => (
        <Card key={t.id} className="p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-slate-900">{t.title || 'Testimonial'}</div>
            <Stars rating={t.rating} />
            <StatusPill status={t.status} />
          </div>
          <div className="text-xs text-slate-500 mt-0.5">By {t.customer?.name} · {t.property?.title}</div>
          <div className="text-sm text-slate-700 mt-2">“{t.body}”</div>
        </Card>
      ))}
    </div>
  );
}
